import React, { useCallback, useState } from 'react';
import { CustomStepper } from '@blumtechgroup/blum-react-core-components';
import {
  Questionnaire,
  QuestionnaireReducerAction,
  validation,
  ValidationErrors,
} from '../../../Common';
import { Box, Container, Paper, styled } from '@mui/material';

const StepDetails = React.lazy(() => import('../StepDetails'));
const StepQuestions = React.lazy(() => import('../StepQuestions'));
const StepReview = React.lazy(() => import('../StepReview'));

const StepperContainer = styled(Container)(({ theme }) => ({
  width: '100%',
}));

const StepperPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
}));

const StepContent = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  minHeight: '300px',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '300px',
  width: '100%',
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
}));

interface Props {
  completeLabel: string;
  questionnaire: Questionnaire;
  dispatch: React.Dispatch<{ type: QuestionnaireReducerAction; value: any }>;
  handleSave: () => void;
}

const QuestionnaireSteps = ({
                              completeLabel,
                              questionnaire,
                              dispatch,
                              handleSave,
                            }: Props): React.ReactElement => {
  const [step, setStep] = useState<number>(0);

  const [errors, setErrors] = useState<ValidationErrors>({
    name: false,
    questions: false,
    send_to: false,
    questionText: {},
    nestedQuestionText: {},
  });

  const validate = useCallback(
    (_step: number) => {
      const errorItems: Record<string, any> = {};

      if (_step === 0) {
        errorItems.name = validation.name(questionnaire.name);
        errorItems.send_to = validation.send_to(questionnaire.send_to);
      }
      if (_step === 1) {
        errorItems.name = validation.name(questionnaire.name);

      }

      setErrors((prevErrors) => ({ ...prevErrors, ...errorItems }));

      return false;

    },
    [questionnaire, setErrors],
  );


  const renderComponent = (Component: React.LazyExoticComponent<any>, props: object) => (
    <React.Suspense fallback={<LoadingContainer>Loading...</LoadingContainer>}>
      <Component {...props} />
    </React.Suspense>
  );

  return (
    <StepperContainer>
      <StepperPaper elevation={3}>
        <CustomStepper
          step={step}
          isNextDisabled={false}
          completeLabel={completeLabel}
          setStep={setStep}
          validate={validate}
          handleComplete={handleSave}
          gridSize={12}
          stepperSteps={[
            {
              label: 'Details',
              gridSize: 6,
              component: (
                <StepContent>
                  {renderComponent(StepDetails, {
                    questionnaire,
                    errors,
                    setErrors,
                    dispatch,
                  })}
                </StepContent>
              ),
            },
            {
              label: 'Questions',
              gridSize: 7,
              component: (
                <StepContent>
                  {renderComponent(StepQuestions, {
                    questionnaire,
                    errors,
                    setErrors,
                    dispatch,
                  })}
                </StepContent>
              ),
            },
            {
              label: 'Review',
              gridSize: 7,
              component: (
                <StepContent>
                  {renderComponent(StepReview, {
                    questionnaire,
                  })}
                </StepContent>
              ),
            },
          ]}
        />
      </StepperPaper>
    </StepperContainer>
  );
};

export default QuestionnaireSteps;
